<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { applyColorScheme, getColorScheme } from 'dashboard/helper/themeHelper';
import AcoesPasso from '../components/AcoesPasso.vue';
import DicaDestaque from '../components/DicaDestaque.vue';
import { BTN, CARD, ENTER, H1, MOTION, SMALL, SUB, TILE, TILE_TONES } from '../ui';

// Tema e tamanho da fonte, aplicados na hora. Reaproveita o que já existe:
// o tema é o mesmo de Aparência (themeHelper, guardado no aparelho) e a fonte
// é ui_settings.font_size, a mesma do perfil.
const emit = defineEmits(['next']);

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const THEMES = [
  { value: 'light', icon: 'i-ph-sun-duotone' },
  { value: 'dark', icon: 'i-ph-moon-duotone' },
  { value: 'auto', icon: 'i-ph-monitor-duotone' },
];
const FONTS = [
  { value: '14px', key: 'SMALL', sample: 'text-sm' },
  { value: '16px', key: 'DEFAULT', sample: 'text-base' },
  { value: '18px', key: 'LARGE', sample: 'text-lg' },
];

const theme = ref(getColorScheme());
const fontSize = computed(() => uiSettings.value?.font_size || '16px');

const chooseTheme = value => {
  theme.value = applyColorScheme(value);
};

const chooseFont = value => {
  document.documentElement.style.setProperty('font-size', value);
  updateUISettings({ font_size: value });
};

const optionClass = active => [
  MOTION,
  'text-left rounded-2xl border p-3 bg-n-card hover:bg-n-slate-2',
  active
    ? 'border-n-blue-9 ring-2 ring-n-blue-9/20'
    : 'border-n-weak',
];

const tips = computed(() => [
  t('ONBOARDING_VISOR.APPEARANCE.TIPS.CHANGE_LATER'),
  t('ONBOARDING_VISOR.APPEARANCE.TIPS.FONT_EVERYWHERE'),
]);
</script>

<template>
  <div>
    <section :class="ENTER">
      <h1 :class="H1">
        <span class="text-n-slate-10">{{ t('ONBOARDING_VISOR.APPEARANCE.HELLO') }}</span>
        <br />
        {{ t('ONBOARDING_VISOR.APPEARANCE.TITLE') }}
      </h1>
      <p :class="SUB">{{ t('ONBOARDING_VISOR.APPEARANCE.SUBTITLE') }}</p>
    </section>

    <div class="grid gap-6 mt-8 lg:grid-cols-[1.55fr_1fr] items-start max-md:gap-4 max-md:mt-5">
      <div class="grid gap-6 max-md:gap-4">
        <section :class="CARD">
          <div class="flex items-center gap-3 mb-4">
            <span :class="[TILE, TILE_TONES.azul]"><Icon icon="i-ph-palette-duotone" /></span>
            <h2 class="text-xl font-medium tracking-[-0.02em]">
              {{ t('ONBOARDING_VISOR.APPEARANCE.THEME') }}
            </h2>
          </div>
          <div class="grid grid-cols-3 gap-3 max-md:grid-cols-1" role="radiogroup">
            <button
              v-for="option in THEMES"
              :key="option.value"
              type="button"
              role="radio"
              :aria-checked="theme === option.value"
              :class="optionClass(theme === option.value)"
              :data-testid="`tema-${option.value}`"
              @click="chooseTheme(option.value)"
            >
              <span class="flex h-20 rounded-xl overflow-hidden border border-n-weak mb-3">
                <span
                  v-if="option.value !== 'dark'"
                  class="flex-1 bg-[#F8F9FA] p-2"
                >
                  <span class="block h-full rounded-md bg-white" />
                </span>
                <span
                  v-if="option.value !== 'light'"
                  class="flex-1 bg-[#0F0F0F] p-2"
                >
                  <span class="block h-full rounded-md bg-[#1A1A1A]" />
                </span>
              </span>
              <span class="flex items-center gap-2 font-medium text-n-slate-12">
                <Icon :icon="option.icon" class="size-5 text-n-slate-10" />
                {{ t(`ONBOARDING_VISOR.APPEARANCE.THEMES.${option.value.toUpperCase()}`) }}
              </span>
            </button>
          </div>
        </section>

        <section :class="CARD">
          <div class="flex items-center gap-3 mb-4">
            <span :class="[TILE, TILE_TONES.azul]"><Icon icon="i-ph-text-aa-duotone" /></span>
            <h2 class="text-xl font-medium tracking-[-0.02em]">
              {{ t('ONBOARDING_VISOR.APPEARANCE.FONT') }}
            </h2>
          </div>
          <div class="grid grid-cols-3 gap-3" role="radiogroup">
            <button
              v-for="option in FONTS"
              :key="option.value"
              type="button"
              role="radio"
              :aria-checked="fontSize === option.value"
              :class="optionClass(fontSize === option.value)"
              :data-testid="`fonte-${option.value}`"
              @click="chooseFont(option.value)"
            >
              <span class="block font-medium text-n-slate-12" :class="option.sample">Aa</span>
              <span :class="SMALL">
                {{ t(`ONBOARDING_VISOR.APPEARANCE.FONTS.${option.key}`) }}
              </span>
            </button>
          </div>
        </section>
      </div>

      <div class="grid gap-6 max-md:gap-4">
        <section :class="CARD" data-testid="previa">
          <p class="mb-3" :class="SMALL">{{ t('ONBOARDING_VISOR.APPEARANCE.PREVIEW') }}</p>
          <div class="rounded-xl bg-n-background p-4 grid gap-2.5">
            <p class="max-w-[85%] rounded-2xl rounded-bl-md bg-n-card border border-n-weak px-3.5 py-2.5 text-n-slate-12">
              {{ t('ONBOARDING_VISOR.APPEARANCE.PREVIEW_CLIENT') }}
            </p>
            <p class="max-w-[85%] justify-self-end rounded-2xl rounded-br-md bg-n-blue-9 text-white px-3.5 py-2.5">
              {{ t('ONBOARDING_VISOR.APPEARANCE.PREVIEW_AI') }}
            </p>
          </div>
        </section>
        <DicaDestaque :title="t('ONBOARDING_VISOR.APPEARANCE.TIPS_TITLE')" :tips="tips" />
      </div>
    </div>

    <AcoesPasso :trust="t('ONBOARDING_VISOR.APPEARANCE.TRUST')">
      <button type="button" :class="BTN" data-testid="seguir" @click="emit('next')">
        {{ t('ONBOARDING_VISOR.APPEARANCE.CONFIRM') }}
        <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
      </button>
    </AcoesPasso>
  </div>
</template>
